import { Component, computed, ElementRef, ViewChild } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import moment from 'moment/moment';
import { DisplayTableService } from '@core/helper/display-table.service';
import { UtilitiesService } from '@core/api/utlities.service';

export type FilterOperator =
  | 'eq' | 'not'
  | 'contains'
  | 'starts_with'
  | 'gt' | 'gte' | 'lt' | 'lte'
  | 'between'
  | 'true' | 'false'
  | 'null' | 'not_null';

export interface ColumnGroup {
  value: string;
  subOptions: string[];
}

export interface PipeConfig {
  name: 'date' | 'currency' | 'number' | 'percent';
  args?: any[];
}

export interface IconConfig {
  class: string;
  icon: string;
}

export interface ColumnDef {
  field?: string;
  label: string;
  headerLabel?: string;
  type?: 'text' | 'number' | 'date' | 'select';
  options?: string[] | ColumnGroup[];                    // for select type
  operators?: FilterOperator[];          // override defaults per column
  exportable?: boolean;
  sortable?: boolean;
  templateColumn?: string;
  quickFilter?: boolean;
  rowValuePipe?: PipeConfig;
  rowSubtitlePipe?: PipeConfig;
  iconMap?: Record<any, IconConfig>;
  columnClass?: string;

  routerLink?(param: any): any;

  rowValue?(param: any): any;

  rowSubtitle?(param: any): any;
}

export interface ActiveFilter {
  id: string;                            // uuid for tracking
  field: string;
  fieldLabel: string;
  operator: FilterOperator;
  value?: any;
  valueTo?: any;                         // for 'between'
  displayLabel: string;                  // "County equals Wicomico"
  query: string;
}

export const OPERATOR_LABELS: Record<FilterOperator, string> = {
  eq: 'equals',
  not: 'does not equal',
  contains: 'contains',
  starts_with: 'starts with',
  gt: '>',
  gte: '>=',
  lt: '<',
  lte: '<=',
  between: 'is between',
  null: 'is empty',
  not_null: 'is not empty',
  true: 'is true',
  false: 'is false',
};

@Component({
  selector: 'fc-display-table-filter',
  templateUrl: './display-table-filter.component.html',
  styleUrls: ['./display-table-filter.component.scss'],
  standalone: false
})
export class DisplayTableFilterComponent {
  @ViewChild('operatorSelect') operatorSelect!: MatSelect;
  private _valueInput?: ElementRef;

  @ViewChild('valueInput') set valueInput(el: ElementRef) {
    if (el) {
      // fires when the element is created in the DOM
      this._valueInput = el;
      setTimeout(() => el.nativeElement?.focus());
    }
  }

  // builder state
  selectedColumn: ColumnDef | null = null;
  selectedOperator: FilterOperator | null = null;
  value: any = null;
  valueTo: any = null;
  operatorLabels = OPERATOR_LABELS;
  editingUUID: string = null;

  availableOperators: FilterOperator[] = [];

  needsValue: boolean = false;

  needsValueTo: boolean = false;

  get isFormValid(): boolean {
    if (!this.selectedColumn || !this.selectedOperator) {
      return false;
    }
    if (!this.needsValue) {
      return true;
    }
    if (this.needsValueTo) {
      return this.value != null && this.valueTo != null;
    }
    return this.value != null && this.value !== '';
  }

  constructor(
    public displayTableService: DisplayTableService,
    readonly utilitiesService: UtilitiesService
  ) {}

  onColumnSelected() {
    // auto-advance focus
    this.availableOperators = this.selectedColumn.operators;
    this.selectedOperator = this.availableOperators[0];
    this.onOperatorSelected();
  }

  setNeedsValue() {
    this.needsValue = !['null', 'not_null', 'true', 'false'].includes(this.selectedOperator);
    this.needsValueTo = this.selectedOperator === 'between';
  }


  onOperatorSelected() {
    this.setNeedsValue();
    if (this.needsValue) {
      setTimeout(() => this._valueInput?.nativeElement.focus(), 50);
    } else {
      this.value = null;
    }
    if (!this.needsValueTo) {
      this.valueTo = null;
    }
  }

  addFilter() {
    if (!this.isFormValid) {
      return;
    }
    if (this.selectedColumn.type == 'date') {
      if (this.needsValue) {
        this.value = moment(this.value, true).format('YYYY-MM-DD')
      }
      if (this.needsValueTo) {
        this.valueTo = moment(this.valueTo, true).format('YYYY-MM-DD')
      }
    }
    this.value = this.utilitiesService.preprocessFilterString(this.value);
    this.valueTo = this.utilitiesService.preprocessFilterString(this.valueTo);
    let filterValue = ',';
    if (['null', 'not_null', 'true', 'false'].includes(this.selectedOperator)) {
      filterValue = '';
    } else {
      filterValue += this.value;
      if (this.selectedOperator == 'between') {
        filterValue += ',' + this.valueTo
      }
    }
    const filter: ActiveFilter = {
      id: this.editingUUID ?? crypto.randomUUID(),
      field: this.selectedColumn.field,
      fieldLabel: this.selectedColumn.label,
      operator: this.selectedOperator,
      value: this.value,
      valueTo: this.valueTo,
      displayLabel: this.buildDisplayLabel(),
      query: `${this.selectedColumn.field}(${this.selectedOperator}${filterValue})`
    };
    if (this.editingUUID) {
      this.displayTableService.setActiveFilters(this.displayTableService.activeFilters()
        .map(f => f.id == this.editingUUID ? filter : f));
    } else {
      this.displayTableService.setActiveFilters([...this.displayTableService.activeFilters(), filter]);
    }
    this.reset();
  }

  removeFilter(id: string) {
    this.displayTableService.setActiveFilters(this.displayTableService.activeFilters().filter(f => f.id !== id));
    if (this.editingUUID === id) {
      this.reset();
    }
  }

  buildDisplayLabel(): string {
    const col = this.selectedColumn.label;
    const op = OPERATOR_LABELS[this.selectedOperator];
    if (!this.needsValue) {
      return `${col} ${op}`;
    }
    if (this.needsValueTo) {
      return `${col} ${op} ${decodeURIComponent(this.value)} and ${decodeURIComponent(this.valueTo)}`;
    }
    if (this.selectedColumn.type == 'select') {
      return `${col} ${op} ${decodeURIComponent(this.value.toString()).replaceAll(',', ' or ')}`;
    }
    return `${col} ${op} ${decodeURIComponent(this.value)}`;
  }

  reset() {
    this.selectedColumn = null;
    this.selectedOperator = null;
    this.value = null;
    this.valueTo = null;
    this.editingUUID = null;
    this.needsValueTo = false;
    this.needsValue = false;
  }

  editFilter(f: ActiveFilter) {
    this.editingUUID = f.id;
    this.selectedColumn = f.field ? this.displayTableService.columnData.find(c => c.field === f.field) ?? null : null;
    this.availableOperators = this.selectedColumn.operators;
    this.selectedOperator = f.operator;
    this.setNeedsValue();
    this.value = decodeURIComponent(this.selectedColumn.type == 'date' ? moment(f.value, true)
      .toDate() : f.value);
    if (this.selectedColumn.type == 'select') {
      this.value = this.value.split(',');
    }
    this.valueTo = decodeURIComponent(this.selectedColumn.type == 'date' ? moment(f.valueTo, true)
      .toDate() : f.valueTo);
  }

  activeFilterColumns = computed(() =>
    new Set(this.displayTableService.activeFilters().map(f => f.field))
  );

  quickFilter(templateColumn: ColumnDef) {
    this.reset();
    let active = this.displayTableService.activeFilters().find(c => c.field === templateColumn.field)
    if (active) {
      this.editFilter(active)
    } else {
      this.selectedColumn = templateColumn;
      this.onColumnSelected();
    }
  }
}
